import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Switch,
    TextField,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import { Save as SaveIcon, Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import masavService from '../../../Services/masav.service';
import s from './MasavDraftModal.module.scss';

const MASAV_SERVER_MESSAGE_TO_I18N = {
    'Bank code missing or invalid (2 digits required).': 'MasavErr_BankCode',
    'Branch number missing or invalid (3 digits required).': 'MasavErr_Branch',
    'Account number missing or invalid (4\u201312 digits required after normalization).': 'MasavErr_AccountNumber',
    'Client name is required for beneficiary name field.': 'MasavErr_ClientNameRequired',
    'Payment day must be between 1 and 31.': 'MasavErr_PaymentDay',
};

function translateMasavServerMessage(msg, t) {
    if (msg == null || msg === '') return '';
    const key = MASAV_SERVER_MESSAGE_TO_I18N[msg];
    return key ? t(key) : msg;
}

function pickClientRow(raw) {
    const day = raw.paymentDayOfMonth ?? raw.PaymentDayOfMonth ?? null;
    return {
        caseId: raw.caseId ?? raw.CaseId ?? '',
        clientName: raw.clientName ?? raw.ClientName ?? '',
        clientId: raw.clientId ?? raw.ClientId ?? '',
        isMasavEnabled: raw.isMasavEnabled ?? raw.IsMasavEnabled ?? false,
        bankCode: raw.bankCode ?? raw.BankCode ?? '',
        branchNumber: raw.branchNumber ?? raw.BranchNumber ?? '',
        accountNumber: raw.accountNumber ?? raw.AccountNumber ?? '',
        paymentDayOfMonth: day == null ? '' : String(day),
    };
}

/** '' → null; otherwise an integer (may be out of range — see isValidPaymentDay). */
function parsePaymentDay(value) {
    const str = String(value ?? '').trim();
    if (!str) return null;
    const n = Number(str);
    return Number.isInteger(n) ? n : NaN;
}

function isValidPaymentDay(value) {
    const n = parsePaymentDay(value);
    return n == null || (n >= 1 && n <= 31);
}

function rowsEqual(a, b) {
    if (!a || !b) return false;
    return (
        !!a.isMasavEnabled === !!b.isMasavEnabled &&
        String(a.bankCode ?? '').trim() === String(b.bankCode ?? '').trim() &&
        String(a.branchNumber ?? '').trim() === String(b.branchNumber ?? '').trim() &&
        String(a.accountNumber ?? '').trim() === String(b.accountNumber ?? '').trim() &&
        String(a.paymentDayOfMonth ?? '').trim() === String(b.paymentDayOfMonth ?? '').trim()
    );
}

function hasBankValues(row) {
    return (
        String(row?.bankCode ?? '').trim() &&
        String(row?.branchNumber ?? '').trim() &&
        String(row?.accountNumber ?? '').trim()
    );
}

function canSaveRow(row) {
    if (!row) return false;
    if (!isValidPaymentDay(row.paymentDayOfMonth)) return false;
    if (row.isMasavEnabled) return hasBankValues(row);
    return true;
}

const MasavClientsManageTab = ({
    /** Tab is visible (modal open + manage tab selected). */
    active,
    accountantId,
    direction = 'rtl',
    onMasavClientsChanged,
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [draftByCase, setDraftByCase] = useState({});
    const [savingCaseId, setSavingCaseId] = useState(null);
    const [savingAll, setSavingAll] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [masavFilter, setMasavFilter] = useState('all');
    const loadSeqRef = useRef(0);
    const loadedAccountantRef = useRef(null);
    const fetchInProgressRef = useRef(false);

    useEffect(() => {
        if (!active || !accountantId) return;
        if (loadedAccountantRef.current === accountantId) return;
        if (fetchInProgressRef.current) return;

        fetchInProgressRef.current = true;
        const seq = ++loadSeqRef.current;
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await masavService.getMasavClients(accountantId);
                if (cancelled || seq !== loadSeqRef.current) return;

                const list = (data.clients ?? data.Clients ?? []).map(pickClientRow);
                const draft = {};
                list.forEach((r) => {
                    draft[r.caseId] = { ...r };
                });
                setRows(list);
                setDraftByCase(draft);
                loadedAccountantRef.current = accountantId;
            } catch (e) {
                if (cancelled || seq !== loadSeqRef.current) return;
                console.error(e);
                setError(e.message || 'Error');
            } finally {
                fetchInProgressRef.current = false;
                if (!cancelled && seq === loadSeqRef.current) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, accountantId]);

    const reloadClients = async () => {
        if (!accountantId || fetchInProgressRef.current) return;
        fetchInProgressRef.current = true;
        loadedAccountantRef.current = null;
        const seq = ++loadSeqRef.current;
        setLoading(true);
        setError(null);
        try {
            const data = await masavService.getMasavClients(accountantId);
            if (seq !== loadSeqRef.current) return;
            const list = (data.clients ?? data.Clients ?? []).map(pickClientRow);
            const draft = {};
            list.forEach((r) => {
                draft[r.caseId] = { ...r };
            });
            setRows(list);
            setDraftByCase(draft);
            loadedAccountantRef.current = accountantId;
        } catch (e) {
            if (seq !== loadSeqRef.current) return;
            console.error(e);
            setError(e.message || 'Error');
        } finally {
            fetchInProgressRef.current = false;
            if (seq === loadSeqRef.current) setLoading(false);
        }
    };

    const savedByCase = useMemo(() => {
        const map = {};
        rows.forEach((r) => {
            map[r.caseId] = r;
        });
        return map;
    }, [rows]);

    const dirtySavableRows = useMemo(() => {
        return rows.filter((r) => {
            const draft = draftByCase[r.caseId];
            const saved = savedByCase[r.caseId];
            if (!draft || !canSaveRow(draft)) return false;
            return !rowsEqual(draft, saved);
        });
    }, [rows, draftByCase, savedByCase]);

    const filteredRows = useMemo(() => {
        let list = rows;
        if (masavFilter === 'enabled') {
            list = list.filter((r) => !!(draftByCase[r.caseId] ?? r).isMasavEnabled);
        } else if (masavFilter === 'disabled') {
            list = list.filter((r) => !(draftByCase[r.caseId] ?? r).isMasavEnabled);
        }
        const q = searchQuery.trim().toLowerCase();
        if (!q) return list;
        return list.filter(
            (r) =>
                String(r.clientName ?? '')
                    .toLowerCase()
                    .includes(q) ||
                String(r.caseId ?? '')
                    .toLowerCase()
                    .includes(q)
        );
    }, [rows, searchQuery, masavFilter, draftByCase]);

    const updateDraft = (caseId, patch) => {
        setDraftByCase((prev) => ({
            ...prev,
            [caseId]: { ...prev[caseId], ...patch },
        }));
    };

    const persistRow = async (caseId, row) => {
        const saved = await masavService.updateMasavClient({
            caseId,
            accountantId,
            isMasavEnabled: !!row.isMasavEnabled,
            bankCode: row.bankCode || null,
            branchNumber: row.branchNumber || null,
            accountNumber: row.accountNumber || null,
            paymentDayOfMonth: parsePaymentDay(row.paymentDayOfMonth),
        });
        const normalized = pickClientRow(saved);
        setRows((prev) => prev.map((r) => (r.caseId === caseId ? { ...r, ...normalized } : r)));
        setDraftByCase((prev) => ({ ...prev, [caseId]: normalized }));
        return normalized;
    };

    const handleSave = async (caseId) => {
        const row = draftByCase[caseId];
        if (!row || !accountantId) return;

        if (row.isMasavEnabled && !hasBankValues(row)) {
            toast.error(t('MasavEnableRequiresBank'));
            return;
        }
        if (!isValidPaymentDay(row.paymentDayOfMonth)) {
            toast.error(t('MasavErr_PaymentDay'));
            return;
        }

        setSavingCaseId(caseId);
        setError(null);
        try {
            await persistRow(caseId, row);
            toast.success(t('MasavClientSaved'));
            onMasavClientsChanged?.();
        } catch (e) {
            console.error(e);
            const msg = translateMasavServerMessage(e.message, t) || e.message || t('Error');
            toast.error(msg);
        } finally {
            setSavingCaseId(null);
        }
    };

    const handleSaveAll = async () => {
        if (!accountantId || dirtySavableRows.length === 0) return;
        setSavingAll(true);
        setError(null);
        let ok = 0;
        let failed = 0;
        try {
            for (const r of dirtySavableRows) {
                const draft = draftByCase[r.caseId];
                if (!draft || !canSaveRow(draft)) continue;
                try {
                    await persistRow(r.caseId, draft);
                    ok += 1;
                } catch (e) {
                    failed += 1;
                    console.error(e);
                }
            }
            if (ok > 0) onMasavClientsChanged?.();
            if (ok > 0 && failed === 0) {
                toast.success(t('MasavSaveAllSuccess', { count: ok }));
            } else if (ok > 0) {
                toast.warning(t('MasavSaveAllPartial', { ok, failed }));
            } else {
                toast.error(t('Error'));
            }
        } finally {
            setSavingAll(false);
        }
    };

    const bodyCellSx = {
        fontSize: '0.8125rem',
        verticalAlign: 'middle',
    };

    if (loading && rows.length === 0) {
        return (
            <div className={s.loading}>
                <CircularProgress className={s.spinner} />
            </div>
        );
    }

    return (
        <div dir={direction}>
            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <p className={s.muted} style={{ marginBottom: 12 }}>
                {t('MasavManageClientsHint')} {t('MasavPaymentDayHint')}
            </p>

            <div className={s.toolbar}>
                <div className={s.toolbarGroup}>
                    <TextField
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('MasavDraftSearchRowsPlaceholder')}
                        inputProps={{ dir: direction }}
                        sx={{ minWidth: 220, bgcolor: '#fff' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={masavFilter}
                        onChange={(_, v) => v != null && setMasavFilter(v)}
                        sx={{
                            bgcolor: '#fff',
                            '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.25 },
                            '& .Mui-selected': { color: 'var(--xtram-blue) !important', bgcolor: 'rgba(48, 79, 255, 0.1) !important' },
                        }}
                    >
                        <ToggleButton value="all">{t('MasavFilterAll')}</ToggleButton>
                        <ToggleButton value="enabled">{t('MasavFilterEnabled')}</ToggleButton>
                        <ToggleButton value="disabled">{t('MasavFilterDisabled')}</ToggleButton>
                    </ToggleButtonGroup>
                </div>
                <div className={s.toolbarGroup}>
                    <button
                        type="button"
                        className={s.secondaryBtn}
                        onClick={reloadClients}
                        disabled={loading || savingAll}
                    >
                        {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                        {t('MasavRefreshDraft')}
                    </button>
                    <button
                        type="button"
                        className={s.primaryBtn}
                        disabled={savingAll || dirtySavableRows.length === 0 || !!savingCaseId}
                        onClick={handleSaveAll}
                    >
                        {savingAll ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                        {t('MasavSaveAll', { count: dirtySavableRows.length })}
                    </button>
                </div>
            </div>

            {rows.length === 0 && !loading ? (
                <div className={s.emptyState}>{t('MasavNoClientsConfigured')}</div>
            ) : filteredRows.length === 0 ? (
                <div className={s.emptyState}>{t('noData')}</div>
            ) : (
                <div className={s.tableWrap} dir={direction} style={{ maxHeight: 480 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('ClientName')}</TableCell>
                                <TableCell>{t('Case')}</TableCell>
                                <TableCell>{t('MasavEnabled')}</TableCell>
                                <TableCell>{t('Bank')}</TableCell>
                                <TableCell>{t('Branch')}</TableCell>
                                <TableCell>{t('AccountNumber')}</TableCell>
                                <TableCell>
                                    <Tooltip title={t('MasavPaymentDayHint')}>
                                        <span>{t('MasavPaymentDay')}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ width: 100 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRows.map((r) => {
                                const draft = draftByCase[r.caseId] ?? r;
                                const isSaving = savingCaseId === r.caseId;
                                const dayInvalid = !isValidPaymentDay(draft.paymentDayOfMonth);
                                return (
                                    <TableRow key={r.caseId} hover>
                                        <TableCell sx={bodyCellSx}>
                                            <span className={s.clientName}>{r.clientName}</span>
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <span className={s.clientCase}>{r.caseId}</span>
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Switch
                                                size="small"
                                                checked={!!draft.isMasavEnabled}
                                                onChange={(e) => updateDraft(r.caseId, { isMasavEnabled: e.target.checked })}
                                                inputProps={{ 'aria-label': t('MasavEnabled') }}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <TextField
                                                size="small"
                                                variant="standard"
                                                value={draft.bankCode ?? ''}
                                                onChange={(e) => updateDraft(r.caseId, { bankCode: e.target.value })}
                                                disabled={!draft.isMasavEnabled}
                                                sx={{ width: 72 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <TextField
                                                size="small"
                                                variant="standard"
                                                value={draft.branchNumber ?? ''}
                                                onChange={(e) => updateDraft(r.caseId, { branchNumber: e.target.value })}
                                                disabled={!draft.isMasavEnabled}
                                                sx={{ width: 88 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <TextField
                                                size="small"
                                                variant="standard"
                                                value={draft.accountNumber ?? ''}
                                                onChange={(e) => updateDraft(r.caseId, { accountNumber: e.target.value })}
                                                disabled={!draft.isMasavEnabled}
                                                sx={{ width: 120 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <TextField
                                                size="small"
                                                variant="standard"
                                                type="number"
                                                value={draft.paymentDayOfMonth ?? ''}
                                                placeholder="—"
                                                onChange={(e) => updateDraft(r.caseId, { paymentDayOfMonth: e.target.value })}
                                                disabled={!draft.isMasavEnabled}
                                                error={dayInvalid}
                                                helperText={dayInvalid ? '1-31' : undefined}
                                                inputProps={{ min: 1, max: 31, step: 1 }}
                                                sx={{ width: 64 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <button
                                                type="button"
                                                className={`${s.primaryBtn} ${s.smallBtn}`}
                                                disabled={isSaving || savingAll || dayInvalid}
                                                onClick={() => handleSave(r.caseId)}
                                            >
                                                {isSaving ? (
                                                    <CircularProgress size={12} color="inherit" />
                                                ) : (
                                                    <SaveIcon fontSize="small" />
                                                )}
                                                {t('Save')}
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
};

export default MasavClientsManageTab;
